import type {
  AiSearchDetailsData,
  GeoAnalysisData,
  GeoDimensionItem,
  GeoDimensionKey
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import { getScoreGaugeVerdict } from '@/app/ContentTools/components/ai-content-analysis/AiLikenessGauge';
import {
  getEeatDimensionLevel,
  getEeatLevelTone
} from '@/app/ContentTools/utils/mapEeatData';

const DIMENSION_META: Record<
  GeoDimensionKey,
  { label: string; order: number; weight: string }
> = {
  citability: { label: 'استخراج‌پذیری محتوا', order: 0, weight: '۳۰٪' },
  structural: { label: 'وضوح پاسخ و ساختار محتوا', order: 1, weight: '۲۰٪' },
  evidence: { label: 'کیفیت شواهد و منابع', order: 2, weight: '۲۰٪' },
  authority: { label: 'هویت و زمینه اعتبار', order: 3, weight: '۱۵٪' },
  technical: { label: 'دسترسی فنی به محتوا', order: 4, weight: '۱۵٪' }
};

const mapAiSearchData = (
  data?: AiSearchDetailsData | null
): GeoAnalysisData | null => {
  if (!data) return null;

  const report = data.ai_search_report;
  const message = data.ai_search?.trim() || '';
  const hasReport =
    report != null &&
    (report.geo_score != null ||
      (report.dimensions && Object.keys(report.dimensions).length > 0) ||
      (report.top_strengths && report.top_strengths.length > 0) ||
      (report.top_gaps && report.top_gaps.length > 0));

  if (!message && !hasReport) return null;

  const dimensions: GeoDimensionItem[] = (
    Object.keys(DIMENSION_META) as GeoDimensionKey[]
  )
    .map(key => {
      const item = report?.dimensions?.[key];
      if (!item || typeof item.score !== 'number') return null;
      return {
        key,
        label: DIMENSION_META[key].label,
        score: item.score,
        level: getEeatDimensionLevel(item.score),
        tone: getEeatLevelTone(item.score),
        weight: DIMENSION_META[key].weight
      };
    })
    .filter((item): item is GeoDimensionItem => item != null)
    .sort(
      (a, b) => DIMENSION_META[a.key].order - DIMENSION_META[b.key].order
    );

  const overallScore =
    typeof report?.geo_score === 'number'
      ? report.geo_score
      : dimensions.length > 0
        ? Math.round(
            dimensions.reduce((sum, item) => sum + item.score, 0) /
              dimensions.length
          )
        : 0;

  return {
    overall_score: overallScore,
    verdict: getScoreGaugeVerdict(overallScore),
    dimensions,
    top_strengths: report?.top_strengths || [],
    top_gaps: report?.top_gaps || [],
    message,
    status: data.ai_search_status
  };
};

export default mapAiSearchData;
